import type { Building } from "building-types/building/buildingTypes";
import type { GeneratedRoof, GeneratedRoofSystem } from "building-types/roofs/generatedRoofSystemTypes";
import type { GenerateRoofSkylightOptions } from "building-types/roofs/roofGenerationTypes";
import type { SkylightPlacement } from "building-types/roofs/roofSystemTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { Skylight, SkylightUnit } from "building-types/settings/roofTypes";
import type { RWTable, Table } from "common/data/table";
import { evalFormulas, resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { evaluateRoofSystems } from "../elements/roofs/evaluateRoofSystems";
import { buildRoofSkylights } from "../elements/roofs/roofSkylights";
import { addRoofComponentInstances } from "../generationTools/components/componentRoofs";
import { makeObjectComparator } from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  roofSystems,
  mirror,
  optionChoices,
  entityDisabledByGroup,
  settings,
  enabledFeatures,
  isolatedEntity,
  idToParents,
  componentToTransforms,
  levels,
  doc: {
    skylights: skylightPlacements,
    optionSets,
    instanceOptionSets,
    roofs: initialRoofs,
    roofSystems: initialRoofSystems,
  },
}: Building) {
  const definitions = { optionSets, instanceOptionSets };
  const resolvedOptionChoices = resolveOptionsInFormula(skylightPlacements, definitions, optionChoices);

  return {
    roofSystems,
    mirror,
    resolvedOptionChoices,
    rawOptionChoices: optionChoices,
    definitions,
    skylightPlacements,
    wallSettings: settings.wall,
    enabledFeatures,
    isolatedEntity,
    idToParents,
    componentToTransforms,
    levels,
    initialRoofs,
    initialRoofSystems,
    entityDisabledByGroup,
  };
}

const compare = makeObjectComparator({ definitions: "shallow", resolvedOptionChoices: "shallow" });

function generate({
  roofSystems,
  mirror,
  skylightPlacements: skylightPlacementsUnevaluated,
  wallSettings,
  resolvedOptionChoices,
  rawOptionChoices,
  definitions,
  enabledFeatures,
  isolatedEntity,
  idToParents,
  componentToTransforms,
  levels,
  initialRoofs,
  initialRoofSystems,
  entityDisabledByGroup,
}: ReturnType<typeof select>): Pick<Building, "roofSystems" | "skylights" | "skylightUnits" | "issues"> {
  const issues: BuildingGenerationIssue[] = [];
  let generatedSkylights: RWTable<Skylight> = {};
  let generatedSkylightUnits: RWTable<SkylightUnit> = {};

  let skylightPlacements: Table<SkylightPlacement>;

  if (enabledFeatures?.modelComponents) {
    const { roofs: evaluatedRoofs, roofSystems: evaluatedRoofSystems } = evaluateRoofSystems({
      initialRoofSystems,
      initialRoofs,
      optionChoices: resolvedOptionChoices,
      entityDisabledByGroup,
    });

    const augmented = addRoofComponentInstances(
      evaluatedRoofSystems,
      evaluatedRoofs,
      initialRoofSystems,
      initialRoofs,
      idToParents,
      componentToTransforms,
      levels,
      isolatedEntity,
      {},
      skylightPlacementsUnevaluated,
      definitions,
      rawOptionChoices,
    );
    skylightPlacements = evalFormulas(
      augmented.skylights,
      (v) => resolvedOptionChoices[v],
      undefined,
      entityDisabledByGroup,
    ) as Table<SkylightPlacement>;
  } else {
    skylightPlacements = evalFormulas(
      skylightPlacementsUnevaluated,
      (v) => resolvedOptionChoices[v],
      undefined,
      entityDisabledByGroup,
    ) as Table<SkylightPlacement>;
  }

  if (!roofSystems) {
    return { roofSystems, skylights: generatedSkylights, skylightUnits: generatedSkylightUnits, issues };
  }

  const newRoofSystems: RWTable<GeneratedRoofSystem> = { ...roofSystems };

  for (const roofSystemId in newRoofSystems) {
    const roofSystem = newRoofSystems[roofSystemId];
    const { roofs } = newRoofSystems[roofSystemId];
    const newRoofs: RWTable<GeneratedRoof> = { ...roofs };

    for (const roofId in roofs) {
      const hostSubRoof = roofs[roofId];

      const filteredSkylightPlacements: SkylightPlacement[] = Object.values(skylightPlacements).filter(
        (x) => x.parentId === hostSubRoof.id && !x.disabled,
      );

      const subGableOpts: GenerateRoofSkylightOptions = {
        wallSettings,
        mirror,
        issues,
        skylightPlacements: filteredSkylightPlacements,
      };

      const { skylights, skylightUnits, subRoof: newSubRoof } = buildRoofSkylights(hostSubRoof, subGableOpts);
      generatedSkylights = { ...generatedSkylights, ...skylights };
      generatedSkylightUnits = { ...generatedSkylightUnits, ...skylightUnits };

      newRoofs[newSubRoof.id] = newSubRoof;
    }

    newRoofSystems[roofSystemId] = {
      ...roofSystem,
      roofs: newRoofs,
    };
  }
  return { roofSystems: newRoofSystems, skylights: generatedSkylights, skylightUnits: generatedSkylightUnits, issues };
}

export default memo(select, generate, compare);
